/**
 * Form đồng ý thu giọng nói (consent v4, 2026-10-03) — dùng chung cho luyện nói
 * trong bài học (`SpeakingPractice`) và bài tập/bài kiểm tra có câu Phát âm
 * (`QuizConsent` khi BE trả 403 `speaking_consent_required`).
 *
 * Nội dung khớp `docs/dev/speaking-consent-text.md` (v4). Dùng thêm cho Luyện giao
 * tiếp với AI (`ConversationPracticeFab`).
 */
import { useState } from "react";
import { Button, Checkbox, Input, Space, Typography, message } from "antd";

import { useGrantSpeakingConsent } from "@/api/learning";

const { Text, Paragraph } = Typography;

export function SpeakingConsentForm({
  grantLabel = "Bật luyện nói",
  onGranted,
}: {
  grantLabel?: string;
  onGranted?: () => void;
}) {
  const grant = useGrantSpeakingConsent();
  const [agree, setAgree] = useState(false);
  const [guardian, setGuardian] = useState("");

  const handleGrant = async () => {
    try {
      await grant.mutateAsync(guardian.trim() || undefined);
      message.success("Đã lưu đồng ý thu âm");
      onGranted?.();
    } catch {
      message.error("Không lưu được đồng ý, thử lại");
    }
  };

  return (
    <div data-testid="speaking-consent-gate">
      <Paragraph>
        Để chấm phát âm, khi bạn bấm ghi âm ứng dụng sẽ <Text strong>thu giọng đọc</Text> của bạn.
        Bản ghi được <Text strong>gửi tới Google Gemini</Text> chỉ để chấm phát âm.
      </Paragraph>
      <Paragraph>
        <ul style={{ marginBottom: 0 }}>
          <li>
            <Text strong>Luyện nói trong bài học:</Text> Hệ thống không lưu bản ghi, chỉ giữ điểm.
          </li>
          <li>
            <Text strong>Câu Phát âm trong bài tập/bài kiểm tra:</Text> Hệ thống{" "}
            <Text strong>lưu bản ghi trên máy chủ của hệ thống</Text> để giảng viên chấm và phúc
            khảo, giữ đến hết học kỳ + 12 tháng rồi tự xoá. Chỉ bạn và giảng viên/quản trị môn học
            nghe được.
          </li>
          <li>
            <Text strong>Luyện giao tiếp với AI:</Text> giọng nói của bạn được truyền{" "}
            <Text strong>trực tiếp tới Google Gemini theo thời gian thực</Text> để AI trò chuyện; hệ
            thống lưu bản ghi cả cuộc hội thoại, lời thoại và kết quả đánh giá để bạn và giảng viên
            xem lại, giữ 12 tháng rồi tự xoá.
          </li>
        </ul>
      </Paragraph>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Input
          placeholder="Nếu bạn dưới 18 tuổi: tên người giám hộ (đã đồng ý)"
          value={guardian}
          onChange={(e) => setGuardian(e.target.value)}
          maxLength={200}
          data-testid="speaking-guardian"
        />
        <Checkbox
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          data-testid="speaking-agree"
        >
          Tôi đã đọc và đồng ý cho thu giọng để chấm phát âm.
        </Checkbox>
        <Button
          type="primary"
          disabled={!agree}
          loading={grant.isPending}
          onClick={handleGrant}
          data-testid="speaking-grant"
        >
          {grantLabel}
        </Button>
      </Space>
    </div>
  );
}
